import React from 'react';
import { Box } from '@mui/material';
import Footer from '../Footer';

interface AuthLayoutProps {
  children: React.ReactNode;
  /** Tall artwork for the side column on md and up. */
  banner?: React.ReactNode;
  /** Short artwork shown above the form on phones, where the tall one cannot fit. */
  mobileBanner?: React.ReactNode;
}

/**
 * Two-column shell for the auth screens.
 *
 * The banner is full-bleed on the left and the form sits on the right, with
 * the footer inside the form column so it never runs underneath the artwork.
 *
 * The two banners are separate images rather than one scaled image: the tall
 * one is sized to the viewport height, so on a phone it would either shrink to
 * an illegible sliver or crop away most of its content.
 */
const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  banner,
  mobileBanner,
}) => (
  <Box
    sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      // Exactly the viewport, not a minimum: #root is already a full-height
      // flex column, so `minHeight: 100vh` here stacked a second viewport's
      // worth of height onto it and made the page scroll.
      height: '100vh',
      maxHeight: '100vh',
      overflow: 'hidden',
      // Stretch the columns to the row's full height. Relying on each column's
      // own `height: 100%` left the banner 17px short of the viewport.
      alignItems: 'stretch',
    }}
  >
    {banner && (
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          // The artwork is scaled by height, so its width only exists after
          // layout. `aspect-ratio` gives the column that same shape up front,
          // so its reported width matches the image — without it the column
          // measured the intrinsic 620px, the form column started 100px too
          // early, and the footer ran underneath the banner.
          flexGrow: 0,
          flexShrink: 0,
          // vh units, not a percentage: the column's width has to be computed
          // from the viewport height the image is scaled to. `width: auto`
          // left flex measuring the intrinsic 620px, so the form column began
          // 100px inside the artwork and the footer ran under it.
          flexBasis: `calc(100vh * ${619 / 928})`,
          width: `calc(100vh * ${619 / 928})`,
          // Still capped, so a very short and wide window cannot let the
          // banner crowd out the form.
          maxWidth: '50%',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
        }}
      >
        {banner}
      </Box>
    )}

    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        // The footer is the last child, so the form takes the space above it
        // and stays vertically centred in what is left.
        justifyContent: 'space-between',
        // Only this column scrolls, and only when the form genuinely does not
        // fit — the window itself stays fixed at one viewport.
        overflowY: 'auto',
      }}
    >
      {mobileBanner && (
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            width: '100%',
            flexShrink: 0,
            // The mobile artwork is a 16:9 landscape image meant to be seen
            // whole, so its height follows its own ratio rather than being
            // cropped to a fixed band. maxHeight keeps it from pushing the
            // form below the fold on a short phone.
            maxHeight: '32vh',
            overflow: 'hidden',
            '& img': {
              width: '100%',
              height: 'auto',
              display: 'block',
            },
          }}
        >
          {mobileBanner}
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '400px' }}>{children}</Box>
      </Box>

      <Footer />
    </Box>
  </Box>
);

export default AuthLayout;
